import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { OrgPortalApi } from '@/services/org.api';
import { OrgPromotionApi } from '@/services/org-promotion.api';
import { OrgAffidavitRow, OrgReportsApi } from '@/services/org-reports.api';
import type { ExportFormat } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select } from '@/components/ui/Input';
import { formatDate } from '@/lib/format';

/**
 * Affidavits — org tier, read-only. Cross-school view of the one real gap
 * closed at school-tier (see AffidavitsPage.tsx) — matches the
 * Departments/Video View report pattern exactly. Class Section/Student are
 * per-school lookups (sections/students live in each school's own
 * schema), so both are disabled until a specific school is picked — same
 * cascade as the Video View/Lessons reports. Management (create/edit/
 * delete) stays school-tier — this view has no write actions at all.
 */
export function OrgAffidavitsPage() {
  const [schoolId, setSchoolId] = useState('');
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [studentId, setStudentId] = useState('');
  const [busy, setBusy] = useState<ExportFormat | null>(null);

  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-org-affidavits-report'],
    queryFn: async () => (await OrgPortalApi.listSchools({ limit: 100 })).items,
  });

  const { data: classes = [] } = useQuery({
    queryKey: ['org-affidavits-report-classes', schoolId],
    queryFn: () => OrgPromotionApi.classesForSchool(schoolId),
    enabled: !!schoolId,
  });
  const sections = classes.find((c) => c.id === classId)?.sections ?? [];

  const { data: students = [] } = useQuery({
    queryKey: ['org-affidavits-report-students', schoolId, sectionId],
    queryFn: () => OrgReportsApi.affidavitStudentsBySection(schoolId, sectionId),
    enabled: !!schoolId && !!sectionId,
  });

  function handleSchoolChange(v: string) {
    setSchoolId(v);
    setClassId('');
    setSectionId('');
    setStudentId('');
  }
  function handleClassChange(v: string) {
    setClassId(v);
    setSectionId('');
    setStudentId('');
  }
  function handleSectionChange(v: string) {
    setSectionId(v);
    setStudentId('');
  }

  const params = {
    schoolId: schoolId || undefined,
    classSectionId: (schoolId && sectionId) || undefined,
    studentId: (schoolId && sectionId && studentId) || undefined,
  };

  const { data: rows = [], isFetching } = useQuery({
    queryKey: ['org-affidavits-report', params],
    queryFn: () => OrgReportsApi.affidavits(params),
  });

  const runExport = async (format: ExportFormat) => {
    if (busy) return;
    setBusy(format);
    try {
      await OrgReportsApi.exportAffidavits(format, params);
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Affidavits"
        description="Student and guardian signed affidavits, across your organization's schools."
        actions={
          <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('xlsx')}
              disabled={!!busy}
              title="Export to Excel"
            >
              {busy === 'xlsx' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-green-600" />}
              Excel
            </button>
            <div className="w-px bg-slate-200" />
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('pdf')}
              disabled={!!busy}
              title="Export to PDF"
            >
              {busy === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-red-600" />}
              PDF
            </button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select className="!w-56" value={schoolId} onChange={(e) => handleSchoolChange(e.target.value)}>
          <option value="">All schools</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <Select
          className="!w-40"
          value={classId}
          onChange={(e) => handleClassChange(e.target.value)}
          disabled={!schoolId}
          title={schoolId ? undefined : 'Select a school to filter by class'}
        >
          <option value="">All Classes</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        <Select
          className="!w-36"
          value={sectionId}
          onChange={(e) => handleSectionChange(e.target.value)}
          disabled={!classId}
          title={classId ? undefined : 'Select a class to filter by section'}
        >
          <option value="">All Sections</option>
          {sections.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <Select
          className="!w-48"
          value={studentId}
          onChange={(e) => setStudentId(e.target.value)}
          disabled={!sectionId}
          title={sectionId ? undefined : 'Select a section to filter by student'}
        >
          <option value="">All Students</option>
          {students.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">School</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Class</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Student</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Guardian</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Signatures</th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Created Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 bg-white">
            {isFetching && !rows.length ? (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-slate-400">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-slate-400">
                  No affidavits found for the selected filters.
                </td>
              </tr>
            ) : (
              rows.map((r: OrgAffidavitRow) => (
                <tr key={`${r.schoolId}:${r.id}`} className="hover:bg-slate-50">
                  <td className="px-4 py-2.5 font-medium text-slate-800">{r.schoolName}</td>
                  <td className="px-4 py-2.5 text-slate-600">{r.className}</td>
                  <td className="px-4 py-2.5 text-slate-700">{r.studentName}</td>
                  <td className="px-4 py-2.5 text-slate-600">{r.guardianName ?? '—'}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1.5">
                      {r.studentSignatureUrl ? (
                        <img src={r.studentSignatureUrl} alt="Student signature" title="Student signature" className="h-8 w-14 rounded border border-slate-200 object-contain" />
                      ) : (
                        <span className="text-xs text-slate-400">no student sig</span>
                      )}
                      {r.guardianSignatureUrl ? (
                        <img src={r.guardianSignatureUrl} alt="Guardian signature" title="Guardian signature" className="h-8 w-14 rounded border border-slate-200 object-contain" />
                      ) : (
                        <span className="text-xs text-slate-400">no guardian sig</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-slate-600">{formatDate(r.createdAt)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
